import { sourceNumber } from '../exam-engine/blueprint.ts';
import { notedQuestions } from '../exam-engine/selectors.ts';
import type { SessionState } from '../exam-engine/state.ts';
import type { OptionId } from '../model/brands.ts';
import type { CanonicalExam, CanonicalQuestion, Option } from '../model/canonical.ts';
import { questionAt } from '../model/canonical.ts';
import type { Response } from '../model/session.ts';

/**
 * The notes, as something a person can actually read later.
 *
 * `session-results.json` already carries the notes, and is the right file for a machine. It is the
 * wrong file for revision: nobody opens a JSON document to remember what confused them. So the
 * notes also come out as Markdown — plain text that reads fine unrendered, and turns into headings
 * and quotes in any editor that renders it.
 *
 * It comes in two shapes, and the difference matters enough to be a choice rather than a default:
 *
 *  - **Without the questions.** Your notes and the number each question has *in the exam file*,
 *    which is enough to open the file and find it again, and which survives the shuffling that
 *    makes a session's own numbering meaningless a week later. This file carries none of the
 *    author's content, so it is yours to do anything with.
 *  - **With the questions.** The prompt, the options as they were shown, what you answered, the
 *    right answer and the explanation — a document you can revise from without the exam file
 *    beside you. It contains someone else's content, so it carries their licence line with it and
 *    the interface says so before writing it.
 */
export interface NotesExportOptions {
  /** Bring the question content along, making the file a study document rather than a reminder. */
  readonly includeQuestions: boolean;
}

export function toNotesMarkdown(
  exam: CanonicalExam,
  state: SessionState,
  options: NotesExportOptions = { includeQuestions: false },
  now: () => Date = () => new Date(),
): string {
  const noted = notedQuestions(state);
  const lines: string[] = [`# Notes — ${escapeInline(exam.meta.title)}`, ''];

  if (state.candidateName !== '') {
    lines.push(`**${escapeInline(state.candidateName)}**  `);
  }
  lines.push(
    `Exam \`${exam.meta.id}\`, content version ${exam.meta.contentVersion}  `,
    `Session started ${state.startedAt}, exported ${now().toISOString()}`,
  );

  // The licence travels with the content, and only with the content: a file of your own notes is
  // not a copy of anybody's exam, but a file that quotes the questions is.
  if (options.includeQuestions) {
    lines.push(`Exam content © its authors — licence: ${exam.meta.license ?? 'not declared'}  `);
    if (exam.meta.authors.length > 0) {
      lines.push(
        `Authors: ${exam.meta.authors.map((author) => escapeInline(author.name)).join(', ')}  `,
      );
    }
  }
  lines.push('');

  if (noted.length === 0) {
    lines.push('No notes were written during this session.', '');
    return lines.join('\n');
  }

  lines.push(
    `${String(noted.length)} of ${String(state.blueprint.questionOrder.length)} questions have a note.`,
    '',
  );

  for (const item of noted) {
    const fileNumber = sourceNumber(exam, item.questionId);
    const marked = item.markedForReview ? ' · marked for review' : '';
    lines.push(`## Question ${String(item.position)} — file Q${String(fileNumber)}${marked}`, '');

    const question = questionAt(exam, item.questionId);
    if (options.includeQuestions && question !== undefined) {
      lines.push(
        ...renderQuestion(question, state, item.questionId),
        '### Your note',
        '',
      );
    }

    lines.push(
      // Quoted, so that a note which itself contains a `#` or a `-` cannot restructure the
      // document around it. Blank lines inside a note keep the quote unbroken.
      ...escapeMarkdown(item.note)
        .split('\n')
        .map((line) => (line.trim() === '' ? '>' : `> ${line}`)),
      '',
    );
  }

  return lines.join('\n');
}

/** A filename that says which exam it belongs to, which shape it is, and sorts by date. */
export function notesFilename(
  exam: CanonicalExam,
  options: NotesExportOptions = { includeQuestions: false },
  now: () => Date = () => new Date(),
): string {
  const slug = exam.meta.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
  const day = now().toISOString().slice(0, 10);
  const kind = options.includeQuestions ? 'notes-with-questions' : 'notes';

  return `${slug === '' ? 'exam' : slug}-${kind}-${day}.md`;
}

/**
 * One question, rendered for revision.
 *
 * Everything a person needs to work out why they were wrong, and nothing that only a machine
 * reads: no ids, no scores as numbers, no timings. Options appear **in the order they were shown**,
 * lettered as they were lettered on screen, because a note saying "I nearly picked C" is about the
 * C the candidate saw.
 */
function renderQuestion(
  question: CanonicalQuestion,
  state: SessionState,
  questionId: string,
): string[] {
  const entry = state.entries[questionId];
  const response = entry?.response ?? null;
  const lines: string[] = ['### The question', ''];

  lines.push(...paragraphs(question.content.prompt));
  if (question.content.instructions !== undefined) {
    lines.push(`*${escapeInline(question.content.instructions)}*`, '');
  }

  // An image is never carried, but the words that stand in for it are — a question whose answer
  // lives only in a picture is unanswerable without them.
  for (const media of question.content.media) {
    if (media.role === 'decorative') {
      continue;
    }
    const described = media.description ?? media.alt;
    if (described !== undefined) {
      lines.push(`> **Image.** ${escapeInline(described)}`, '');
    }
  }

  lines.push(...renderInteraction(question, state, questionId, response));

  const outcome = state.scores[questionId]?.outcome;
  if (outcome !== undefined) {
    lines.push(`**Result:** ${outcome}`, '');
  }

  if (question.explanation !== undefined) {
    const { summary, detail } = question.explanation;
    if (summary !== undefined || detail !== undefined) {
      lines.push('### Explanation', '');
      if (summary !== undefined) {
        lines.push(...paragraphs(summary));
      }
      if (detail !== undefined) {
        lines.push(...paragraphs(detail));
      }
    }
  }

  if (question.references.length > 0) {
    lines.push('### References', '');
    for (const reference of question.references) {
      const parts = [reference.title, reference.publisher, reference.locator]
        .filter((part): part is string => part !== undefined)
        .map(escapeInline);
      lines.push(
        reference.url === undefined
          ? `- ${parts.join(' — ')}`
          : `- [${parts.join(' — ')}](${reference.url})`,
      );
    }
    lines.push('');
  }

  return lines;
}

function renderInteraction(
  question: CanonicalQuestion,
  state: SessionState,
  questionId: string,
  response: Response | null,
): string[] {
  const shown = state.blueprint.optionOrder[questionId];

  switch (question.type) {
    case 'single-choice': {
      const chosen = response?.type === 'single-choice' ? response.optionId : null;
      return [
        ...choiceList(question.interaction.options, shown, (option) => [
          option.id === question.interaction.correctOptionId ? 'correct' : null,
          option.id === chosen ? 'your answer' : null,
        ]),
        '',
      ];
    }

    case 'multiple-choice': {
      const chosen = response?.type === 'multiple-choice' ? response.optionIds : [];
      return [
        ...choiceList(question.interaction.options, shown, (option) => [
          question.interaction.correctOptionIds.includes(option.id) ? 'correct' : null,
          chosen.includes(option.id) ? 'your answer' : null,
        ]),
        '',
      ];
    }

    case 'matching': {
      const given = response?.type === 'matching' ? response.pairs : [];
      const label = (options: readonly Option[], id: string): string =>
        options.find((option) => option.id === id)?.text ?? id;

      return [
        ...question.interaction.pairs.map((pair) => {
          const mine = given.find((entry) => entry.itemId === pair.itemId);
          const suffix =
            mine === undefined
              ? ' *(you left this blank)*'
              : mine.targetId === pair.targetId
                ? ''
                : ` *(you chose ${escapeInline(label(question.interaction.targets, mine.targetId))})*`;
          return `- **${escapeInline(label(question.interaction.items, pair.itemId))}** → ${escapeInline(label(question.interaction.targets, pair.targetId))}${suffix}`;
        }),
        '',
      ];
    }

    case 'ordering': {
      const given = response?.type === 'ordering' ? response.order : [];
      const label = (id: string): string =>
        question.interaction.items.find((item) => item.id === id)?.text ?? id;

      const lines = [
        '**Correct order**',
        '',
        ...question.interaction.correctOrder.map(
          (id, index) => `${String(index + 1)}. ${escapeInline(label(id))}`,
        ),
        '',
      ];
      if (given.length > 0) {
        lines.push(
          '**Your order**',
          '',
          ...given.map((id, index) => `${String(index + 1)}. ${escapeInline(label(id))}`),
          '',
        );
      }
      return lines;
    }

    case 'text': {
      const typed = response?.type === 'text' ? response.text : '';
      const lines: string[] = [];

      if (question.interaction.evaluation === 'auto' && question.interaction.accepted.length > 0) {
        lines.push(
          `**Accepted:** ${question.interaction.accepted
          .map((value) => `\`${value.replaceAll('`', "'")}\``)
          .join(', ')}`,
          '',
        );
      }
      if (question.interaction.sampleAnswer !== undefined) {
        lines.push('**Sample answer**', '', ...paragraphs(question.interaction.sampleAnswer));
      }
      lines.push('**Your answer**', '', ...paragraphs(typed === '' ? '(no answer given)' : typed));
      return lines;
    }
  }
}

/**
 * The options, lettered as they were on screen.
 *
 * `shown` is the order the blueprint drew; without it the options are listed as authored, which is
 * also what the candidate saw when shuffling was off.
 */
function choiceList(
  options: readonly Option[],
  shown: readonly string[] | undefined,
  markersFor: (option: Option) => readonly (string | null)[],
): string[] {
  const ordered =
    shown === undefined
      ? options
      : shown
          .map((id) => options.find((option) => option.id === (id as OptionId)))
          .filter((option): option is Option => option !== undefined);

  return ordered.map((option, index) => {
    const letter = String.fromCharCode(65 + index);
    const markers = markersFor(option).filter((marker): marker is string => marker !== null);
    const suffix = markers.length === 0 ? '' : ` *(${markers.join(', ')})*`;
    // Newlines inside an option would break the list item in two, so they become spaces.
    return `- **${letter}.** ${escapeInline(option.text.replace(/\s*\n\s*/g, ' '))}${suffix}`;
  });
}

/**
 * Untrusted text, made inert as Markdown.
 *
 * Everything written into this file that came from an exam is somebody else's content, and this
 * file gets opened somewhere else. Inside the player that content is rendered as text and can do
 * nothing. In a Markdown viewer, raw HTML is commonly enabled — so a prompt containing an `img`
 * tag pointing at a remote server would tell its author exactly when the file was read, and a link
 * with a `javascript:` target is the same trick with different punctuation.
 *
 * So the characters that give Markdown and HTML their power are backslash-escaped. CommonMark
 * renders an escaped `<` as a literal `<`, so nothing is lost to read and nothing is left to act:
 * the escapes are invisible in the rendered document, which is what this file is for.
 */
export function escapeInline(text: string): string {
  return text.replaceAll('\\', '\\\\').replace(/([<>[\]`*_|~])/g, '\\$1');
}

/**
 * The same, plus the punctuation that only means something at the start of a line.
 *
 * `#` makes a heading, `-` a list, `>` a quote, `1.` a numbered item — but only in the first
 * column. Text that will sit in the middle of a line uses `escapeInline` instead, so ordinary
 * prose does not come out peppered with backslashes it never needed.
 */
export function escapeMarkdown(text: string): string {
  return escapeInline(text)
    .replace(/^(\s*)([#+-])/gm, '$1\\$2')
    .replace(/^(\s*)(\d+)\./gm, '$1$2\\.');
}

/** Text as Markdown paragraphs, with a blank line after, and no heading it could be mistaken for. */
function paragraphs(text: string): string[] {
  return [...escapeMarkdown(text).split('\n').map((line) => (line.trim() === '' ? '' : line)), ''];
}
